import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Folder, FolderPlus, ChevronRight, Sparkles, FileText, Trash2,
  ExternalLink, Loader2, Search, FileSignature,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ContactFilesPanel — embedded mini Documents browser scoped to a single
 * contact's root folder. Drops into the Contact / Vendor profile to keep
 * the folder system synced with the Documents Hub.
 *
 * Props:
 *  - workspaceId
 *  - contactId
 *  - contactType: 'customer' | 'vendor'
 */
export default function ContactFilesPanel({ workspaceId, contactId, contactType = 'customer' }) {
  const space = contactType === 'vendor' ? 'vendor' : 'client';
  const [rootFolder, setRootFolder] = useState(null);
  const [allFolders, setAllFolders] = useState([]); // every folder in this contact's tree
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [crumb, setCrumb] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [showApply, setShowApply] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [applying, setApplying] = useState(false);
  const [search, setSearch] = useState('');

  const fetchTree = useCallback(async () => {
    if (!workspaceId || !contactId) return;
    setLoading(true);
    try {
      // Pull ALL folders in this space, then filter to this contact's subtree
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/folders?space=${space}`);
      if (res.ok) {
        const data = await res.json();
        // Find the root linked to this contact
        const root = data.find(f =>
          f.is_contact_root && (f.linked_contact_ids || []).includes(contactId)
        );
        setRootFolder(root || null);
        // Filter to the subtree: any folder that is the root, OR has the root as ancestor
        if (root) {
          const ids = new Set([root.folder_id]);
          let added = true;
          while (added) {
            added = false;
            for (const f of data) {
              if (f.parent_folder_id && ids.has(f.parent_folder_id) && !ids.has(f.folder_id)) {
                ids.add(f.folder_id);
                added = true;
              }
            }
          }
          setAllFolders(data.filter(f => ids.has(f.folder_id)));
        } else {
          setAllFolders([]);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [workspaceId, contactId, space]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Fetch documents in current folder
  useEffect(() => {
    if (!currentFolderId) {
      setDocs([]);
      return;
    }
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/folders/${currentFolderId}/documents`);
        if (res.ok) setDocs(await res.json());
      } catch {}
    })();
  }, [currentFolderId]);

  // Children of the current location
  const childFolders = useMemo(() => {
    const parentId = currentFolderId ?? rootFolder?.folder_id;
    let kids = allFolders.filter(f => f.parent_folder_id === parentId);
    if (search) kids = kids.filter(f => (f.name || '').toLowerCase().includes(search.toLowerCase()));
    return kids;
  }, [allFolders, currentFolderId, rootFolder, search]);

  // When the user has drilled into a contract-flavoured subfolder, surface a
  // "Send for signature" CTA that deep-links into the Signatures wizard with
  // the contact and destination folder pre-filled. Saves the back-and-forth
  // between Contacts → Signatures.
  const currentFolder = useMemo(() => {
    if (!currentFolderId) return null;
    return allFolders.find(f => f.folder_id === currentFolderId) || null;
  }, [currentFolderId, allFolders]);

  const showSendForSignature = useMemo(() => {
    const name = (currentFolder?.name || '').toLowerCase();
    return /contract|agreement|signed|signature|engagement|nda/.test(name);
  }, [currentFolder]);

  const signaturesLink = useMemo(() => {
    const folderId = currentFolderId ?? rootFolder?.folder_id;
    const params = new URLSearchParams();
    params.set('tab', 'new');
    if (contactId) params.set('contact_id', contactId);
    if (folderId) params.set('folder_id', folderId);
    return `/signatures?${params.toString()}`;
  }, [contactId, currentFolderId, rootFolder]);

  const drillInto = (f) => {
    setCurrentFolderId(f.folder_id);
    setCrumb([...crumb, f]);
  };
  const goTo = (idx) => {
    if (idx === -1) { setCurrentFolderId(null); setCrumb([]); }
    else {
      const next = crumb.slice(0, idx + 1);
      setCrumb(next);
      setCurrentFolderId(next[next.length - 1]?.folder_id || null);
    }
  };

  const handleNewFolder = async () => {
    if (!newName.trim() || !rootFolder) return;
    try {
      const body = {
        name: newName.trim(),
        parent_folder_id: currentFolderId ?? rootFolder.folder_id,
        space,
        linked_contact_ids: [contactId],
      };
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Folder created');
        setNewName(''); setShowNew(false);
        fetchTree();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to create folder');
      }
    } catch { toast.error('Failed to create folder'); }
  };

  const handleDeleteFolder = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? Documents inside will be unlinked, not deleted.`)) return;
    try {
      const res = await apiFetch(`${API_URL}/api/folders/${f.folder_id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Folder deleted'); fetchTree(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  const openApply = async () => {
    setShowApply(true);
    try {
      const res = await apiFetch(`${API_URL}/api/folder-templates?workspace_id=${workspaceId}&include_presets=true`);
      if (res.ok) setTemplates(await res.json());
    } catch {}
  };

  const handleApply = async () => {
    if (!selectedTemplate || !rootFolder) { toast.error('Pick a template'); return; }
    setApplying(true);
    try {
      const target = currentFolderId ?? rootFolder.folder_id;
      const res = await apiFetch(`${API_URL}/api/folder-templates/${selectedTemplate}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, target_parent_folder_id: target }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created_count} folder${data.created_count === 1 ? '' : 's'}`);
        setShowApply(false); setSelectedTemplate('');
        fetchTree();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to apply');
      }
    } catch { toast.error('Failed to apply'); }
    finally { setApplying(false); }
  };

  if (loading) return <div className="p-8 text-center text-sm text-muted-foreground">Loading files…</div>;

  if (!rootFolder) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-2">
          <Folder className="w-10 h-10 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No folder yet for this {contactType}. It will be created automatically next time you save a document, or you can apply a template now.
          </p>
          <Button size="sm" onClick={openApply} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="files-create-root-apply">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Apply Template
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="contact-files-panel">
      {/* Breadcrumb + Actions */}
      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <button onClick={() => goTo(-1)} className="text-sm flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="files-bc-root">
            <Folder className="w-4 h-4" style={{ color: rootFolder.color || '#10b981' }} />
            <span className="font-medium">{rootFolder.name}</span>
          </button>
          {crumb.map((b, idx) => (
            <div key={b.folder_id} className="flex items-center gap-2">
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              <button onClick={() => goTo(idx)} className="text-sm hover:text-primary">{b.name}</button>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {showSendForSignature && (
              <Button
                size="sm"
                asChild
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="files-send-for-signature-btn"
                title="Send a PDF in this folder for digital signature"
              >
                <Link to={signaturesLink}>
                  <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Send for Signature
                </Link>
              </Button>
            )}
            <Button size="sm" variant="outline" asChild>
              <Link to="/documents" data-testid="files-open-in-docs">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in Documents
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={openApply} data-testid="files-apply-template-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Apply Template
            </Button>
            <Button size="sm" onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="files-new-folder-btn">
              <FolderPlus className="w-3.5 h-3.5 mr-1.5" /> New Folder
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search inside this contact's files…" className="pl-10" data-testid="files-search" />
      </div>

      {showNew && (
        <Card>
          <CardContent className="p-3 flex items-center gap-2">
            <FolderPlus className="w-4 h-4 text-muted-foreground" />
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleNewFolder()}
              placeholder="Folder name" className="flex-1 h-9" autoFocus data-testid="files-new-folder-name" />
            <Button size="sm" onClick={handleNewFolder} data-testid="files-new-folder-submit">Create</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowNew(false); setNewName(''); }}>Cancel</Button>
          </CardContent>
        </Card>
      )}

      {showApply && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <Sparkles className="w-4 h-4 text-primary" />
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="w-72 h-9" data-testid="files-apply-template-select">
                <SelectValue placeholder="Pick a template to apply…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} {t.is_system ? '(system)' : ''} · {t.node_count || t.nodes?.length || 0} folders
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleApply} disabled={!selectedTemplate || applying} data-testid="files-apply-template-confirm">
              {applying ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
              Apply Here
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowApply(false); setSelectedTemplate(''); }}>Cancel</Button>
          </CardContent>
        </Card>
      )}

      {childFolders.length === 0 && docs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-sm text-muted-foreground">
            Empty folder. Add subfolders, apply a template, or save documents here from any AI Hub by attaching this {contactType}.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {childFolders.map(f => (
            <Card key={f.folder_id} onClick={() => drillInto(f)}
              className="group cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
              data-testid={`files-folder-${f.folder_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${f.color || '#10b981'}20`, color: f.color || '#10b981' }}>
                  <Folder className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sm truncate block">{f.name}</span>
                  <p className="text-[10px] text-muted-foreground">{f.document_count || 0} doc{f.document_count === 1 ? '' : 's'}</p>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive"
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </CardContent>
            </Card>
          ))}
          {currentFolderId && docs.map(d => (
            <Card key={d.document_id} className="hover:shadow-md transition-all">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sm truncate block">{d.title || 'Untitled'}</span>
                  <p className="text-[10px] text-muted-foreground">
                    {d.hub && <Badge variant="outline" className="text-[9px] capitalize mr-1.5">{d.hub}</Badge>}
                    {d.updated_at && new Date(d.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
